import React from "react";

/**
 * Tiny inline SVG sparkline for KPI cards.
 *
 * Props:
 * - data: Array<number> — data points (oldest → newest). >= 2 Elemente.
 * - width/height: Pixel-Größe (Default 80×24).
 * - stroke: Linien-Farbe (Default currentColor).
 * - fill: optionale Füllfarbe unter der Linie.
 * - className: optional.
 */
export function Sparkline({
  data,
  width = 80,
  height = 24,
  stroke = "currentColor",
  fill = "none",
  strokeWidth = 1.5,
  className = "",
  "data-testid": testid,
}) {
  const pts = Array.isArray(data) ? data.filter((v) => v != null && !Number.isNaN(v)) : [];
  if (pts.length < 2) {
    return (
      <svg
        width={width}
        height={height}
        className={className}
        data-testid={testid}
        aria-hidden="true"
      >
        <line
          x1={0}
          y1={height / 2}
          x2={width}
          y2={height / 2}
          stroke={stroke}
          strokeWidth={1}
          strokeDasharray="2 2"
          opacity={0.3}
        />
      </svg>
    );
  }

  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const range = max - min || 1;
  const padY = 2;
  const step = width / (pts.length - 1);

  const coords = pts.map((v, i) => {
    const x = i * step;
    const y = height - padY - ((v - min) / range) * (height - 2 * padY);
    return [x, y];
  });

  const path = coords
    .map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`)
    .join(" ");

  const areaPath =
    fill && fill !== "none"
      ? `${path} L${width.toFixed(2)},${height} L0,${height} Z`
      : null;

  const lastX = coords[coords.length - 1][0];
  const lastY = coords[coords.length - 1][1];

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={className}
      data-testid={testid}
      aria-hidden="true"
      style={{ overflow: "visible" }}
    >
      {areaPath ? <path d={areaPath} fill={fill} opacity={0.2} /> : null}
      <path
        d={path}
        fill="none"
        stroke={stroke}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r={1.75} fill={stroke} />
    </svg>
  );
}

export default Sparkline;
